import React, { useState, useEffect } from 'react';
import { NextPageContext, NextPage } from 'next';
import { AppContext } from 'next/app';
import ApolloClient, { NormalizedCacheObject } from 'apollo-boost';
import triggerTracking from '@blueprint/tracking';
import Router, { useRouter } from 'next/router';
import { ApolloError } from 'apollo-boost';

import SearchResults from '../SearchResults';
import graphQLWrapper from '../../shared/graphql/withApollo';
import SearchResultsLayout from '../Layout';
import { LISTINGS_QUERY } from '../../queries/search/listings';
import { REDIRECT_QUERY } from '../../queries/search/redirects';
import { removeEverythingAfterHash, snakeCaseKeys } from '../../shared/helpers';
import { mapTargetingFilters } from 'shared/components/Adverts/lib/coerceDfpAdTargetingObject';
import { getTranslator } from '../../shared/i18n/getTranslator';

import {
    updateTotalResults,
    updatePageNumber,
    updateTotalResultsWasLimited,
    updatePolyenc
} from '../../reducers/searchResults';
import { updateFilters, setEnabledFilters } from '../../reducers/filters';
import { initialiseSortOrder } from '../../reducers/listingsSortOrder';

import {
    SearchProvider,
    searchReducer,
    mapDataToActions,
    Redirect,
    ServerData,
    SearchContextType,
    AnalyticsTaxonomy,
    SeoBlurb,
    generateInitialSearchState,
    SearchType
} from '../SearchContext';
import {
    formatListings,
    buildCanonicalPath,
    initialiseReduxFilters,
    getFeaturedListingsToDisplay,
    buildQueryParamsString,
    buildUrlFromPathAndQueryParams
} from '../helpers';
import { Listing } from '../Listings/Listings.types';
import { DfpTargeting } from 'shared/components/Adverts/types';
import { PropertyOrPromoItem } from '../helpers/featuredProperties';
import searchTranslations from 'public/locales/en/search.json';
import { ANALYTICS_BASE_TAXONOMY } from 'shared/tracking/constants';
import { GraphQLError } from 'graphql';
import { saveSearchAndAlertUrlFixer } from '../helpers/saveSearchAndAlertUrlFixer';
import { Links } from '../SearchContext';

const t = getTranslator(searchTranslations);
interface NextPageContextWithApollo extends NextPageContext {
    apolloClient: ApolloClient<NormalizedCacheObject> | null;
    apolloState: NormalizedCacheObject;
    ctx: NextPageContextApp;
}

type NextPageContextApp = NextPageContextWithApollo & AppContext;

export type SearchPageProps = {
    searchResults?: ServerData['searchResults'];
    discoverMore?: ServerData['discoverMore'];
    breadcrumbs?: ServerData['breadcrumbs'];
    initialState: SearchContextType;
    filtersTargeting?: DfpTargeting;
    analyticsTaxonomy?: AnalyticsTaxonomy;
    regularListingsFormatted?: Listing[];
    extendedListingsFormatted?: Listing[];
    featuredProperties?: PropertyOrPromoItem[];
    transactionType?: SeoBlurb['transactionType'];
    canonicalPath?: string;
    error?: ApolloError;
    links?: Links;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const handleRedirects = (redirects: Redirect[], res: any) => {
    const { redirectUri, code } = redirects[0];

    // always a server redirect as we don't route to routes that need
    // redirects internally i.e. on the browser
    if (res) {
        res.writeHead(code, { Location: redirectUri });
        res.end();
    }
};

type SearchPageFactoryProps = {
    type: SearchType;
};

export const getServerRenderedData = (
    serverSideData: ServerData,
    initialState: SearchContextType
) => {
    const {
        searchResults: {
            metaInfo,
            listings,
            pagination: { totalResults, pageNumber, totalResultsWasLimited },
            sortOrder,
            filters: { fields },
            analyticsTaxonomy,
            links,
            polyenc,
            seoBlurb: { transactionType }
        }
    } = serverSideData;
    const { regular, extended, featured } = listings;

    const searchContextData = mapDataToActions(serverSideData).reduce(
        searchReducer,
        initialState
    );

    const canonicalPath = buildCanonicalPath(metaInfo.canonicalUri || '');

    const formattedLinks = {
        createAlert: saveSearchAndAlertUrlFixer(links?.createAlert),
        saveSearch: saveSearchAndAlertUrlFixer(links?.saveSearch),
        mapView: saveSearchAndAlertUrlFixer(links?.mapView)
    };

    return {
        regularListingsFormatted: formatListings(regular),
        extendedListingsFormatted: formatListings(extended),
        featuredProperties: getFeaturedListingsToDisplay(featured || []),
        searchContextData,
        canonicalPath,
        totalResults,
        sortOrder,
        filtersFields: fields,
        pageNumber,
        totalResultsWasLimited,
        analyticsTaxonomy,
        polyenc,
        filtersTargeting: mapTargetingFilters(fields),
        transactionType,
        links: formattedLinks
    };
};

const isGeoGraphqlError = (err: GraphQLError): boolean => {
    const errors = err?.extensions?.response?.body?.errors;

    if (!Array.isArray(errors)) {
        return false;
    }

    const { validation_errors = [] } = errors[0];

    if (!Array.isArray(validation_errors)) {
        return false;
    }

    const { field } = validation_errors[0] || {};

    return field === 'geo_identifier';
};

const getGeoError = (error: ApolloError) => {
    const { graphQLErrors = [] } = error;

    return graphQLErrors.find((err) => isGeoGraphqlError(err));
};

const SearchPageFactory = (props: SearchPageFactoryProps) => {
    const SearchPage: NextPage<SearchPageProps> = ({
        analyticsTaxonomy,
        initialState,
        regularListingsFormatted,
        extendedListingsFormatted,
        featuredProperties,
        canonicalPath,
        filtersTargeting,
        transactionType,
        searchResults,
        breadcrumbs,
        discoverMore,
        links,
        error
    }) => {
        const [loading, setLoading] = useState(false);
        const router = useRouter();

        const { query } = router;
        const startLoading = () => setLoading(true);
        const stopLoading = () => setLoading(false);

        useEffect(() => {
            // Handling loading while using getInitialProps https://github.com/vercel/next.js/issues/3822
            Router.events.on('routeChangeStart', startLoading);
            Router.events.on('routeChangeComplete', stopLoading);

            return () => {
                Router.events.off('routeChangeStart', startLoading);
                Router.events.off('routeChangeComplete', stopLoading);
            };
        }, [Router.events]);

        if (typeof window !== 'undefined' && analyticsTaxonomy) {
            const addTaxonomyEarlyEvent = {
                event: 'GAEvent',
                eventCategory: 'search results',
                eventAction: 'populate',
                eventLabel: 'react_taxonomy',
                taxonomy: {
                    ...ANALYTICS_BASE_TAXONOMY,
                    ...snakeCaseKeys(analyticsTaxonomy)
                }
            };
            triggerTracking(addTaxonomyEarlyEvent);
        }

        const { dfpAdTargeting, seoAccordions } = initialState;

        const currentPage =
            typeof query.pn === 'string' ? parseInt(query.pn) : 1;

        const numberOfRegularListings =
            regularListingsFormatted && regularListingsFormatted.length
                ? regularListingsFormatted.length
                : 0;

        return (
            <SearchProvider initialState={initialState}>
                <SearchResultsLayout
                    dfpAdTargeting={{ ...dfpAdTargeting, ...filtersTargeting }}
                    seoAccordions={seoAccordions}
                    numberOfRegularListings={numberOfRegularListings}
                    pageNumber={currentPage}
                    links={links as Links}
                >
                    <SearchResults
                        regularListings={regularListingsFormatted || []}
                        extendedListings={extendedListingsFormatted || []}
                        featuredProperties={featuredProperties || []}
                        canonicalPath={canonicalPath || ''}
                        searchResults={
                            searchResults || ({} as ServerData['searchResults'])
                        }
                        breadcrumbs={
                            breadcrumbs || ([] as ServerData['breadcrumbs'])
                        }
                        discoverMore={
                            discoverMore || ({} as ServerData['discoverMore'])
                        }
                        transactionType={transactionType || 'sale'}
                        loading={loading}
                        error={error}
                        links={links as Links}
                    />
                </SearchResultsLayout>
            </SearchProvider>
        );
    };

    SearchPage.getInitialProps = async (ctx: NextPageContextApp) => {
        const { res, asPath, apolloClient, store, query, req } = ctx;
        const path = removeEverythingAfterHash(asPath);
        const searchType = props.type;
        const initialSearchState = generateInitialSearchState({ searchType });

        const isServer = !!req;

        if (isServer) {
            const { data: redirectsData = { redirects: [] } } =
                (await apolloClient?.query({
                    query: REDIRECT_QUERY,
                    variables: { path }
                })) || {};

            const { redirects } = redirectsData;

            const shouldRedirect = redirects && redirects.length;
            if (shouldRedirect) {
                handleRedirects(redirects, res);

                return {
                    initialState: initialSearchState
                };
            }
        }

        try {
            let newPath = asPath;
            // Nasty development nextjs issue with asPath not having query params
            // when getInitialProps is called on the client
            // https://github.com/vercel/next.js/issues/21267
            const isDev = process.env.NODE_ENV !== 'production';
            if (isDev && !isServer) {
                // pagination won't work with dev build while this bug exists
                const nextQueryParameters = buildQueryParamsString(query, 1);
                newPath = buildUrlFromPathAndQueryParams(
                    path,
                    nextQueryParameters
                );
            }

            const { data } =
                (await apolloClient?.query({
                    query: LISTINGS_QUERY,
                    variables: { path: newPath }
                })) || {};

            const { searchResults, breadcrumbs, discoverMore } = data;
            const {
                regularListingsFormatted,
                extendedListingsFormatted,
                featuredProperties,
                searchContextData,
                canonicalPath,
                totalResults,
                sortOrder,
                filtersFields,
                pageNumber,
                totalResultsWasLimited,
                analyticsTaxonomy,
                transactionType,
                filtersTargeting,
                polyenc,
                links
            } = getServerRenderedData(data, initialSearchState);

            const reduxServerSideDispatch = store.dispatch;

            // sort order
            reduxServerSideDispatch(initialiseSortOrder(t, sortOrder));
            // filters
            const filtersFormatted = initialiseReduxFilters(
                filtersFields,
                query
            );

            reduxServerSideDispatch(updateFilters(filtersFormatted));

            reduxServerSideDispatch(setEnabledFilters());

            // Search results info
            reduxServerSideDispatch(updateTotalResults(totalResults));
            reduxServerSideDispatch(updatePageNumber(pageNumber));
            reduxServerSideDispatch(
                updateTotalResultsWasLimited(totalResultsWasLimited)
            );

            reduxServerSideDispatch(updatePolyenc(polyenc));

            return {
                analyticsTaxonomy: analyticsTaxonomy,
                initialState: searchContextData || initialSearchState,
                regularListingsFormatted: regularListingsFormatted,
                extendedListingsFormatted: extendedListingsFormatted,
                featuredProperties,
                canonicalPath: canonicalPath,
                transactionType,
                searchResults,
                breadcrumbs,
                discoverMore,
                filtersTargeting,
                links
            };
        } catch (error) {
            const geoError = getGeoError(error);
            if (geoError) {
                const errLocation: string =
                    geoError?.extensions?.response?.body?.errors[0]
                        ?.validation_errors[0]?.current_value;

                const section = asPath?.includes('/for-sale')
                    ? 'for-sale'
                    : 'to-rent';

                if (isServer) {
                    const redirect: Redirect = {
                        redirectUri: `/search/?section=${section}&q=${
                            errLocation || ''
                        }`,
                        code: 302
                    };
                    handleRedirects([redirect], res);
                } else {
                    await Router.push({
                        pathname: '/search/',
                        query: { section, q: errLocation }
                    });
                }
            }

            return {
                initialState: initialSearchState,
                error
            };
        }
    };

    return {
        // SearchPage exported here just for testing
        SearchPage,
        SearchPageWithApollo: graphQLWrapper(SearchPage)
    };
};

export default SearchPageFactory;
